import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./auth/AuthContext";
import { useAuth } from "./auth/AuthContext";
import AnalyticsTracker from "./components/AnalyticsTracker";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import Forgot from "./pages/Forgot";
import Reset from "./pages/Reset";
import MapPage from "./pages/MapPage";
import NotFoundPage from "./pages/NotFoundPage";
import FishesPage from "./pages/FishesPage";
import FishDetailPage from "./pages/FishDetailPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import ToolsPage from "./pages/ToolsPage";
import FishDetectionPage from "./pages/FishDetectionPage";
import SonarUploadPage from "./pages/SonarUploadPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import Profile from "./pages/Profile";
import PublicSpotPage from "./pages/PublicSpotPage";
import NewSpotPage from "./pages/NewSpotPage";
import EditSpotSuggestionPage from "./pages/EditSpotSuggestionPage";
import { MESSAGING_ENABLED } from "./config/messagingEnabled";
import { MessagesProvider, MessagesPageLazy } from "./messages/integration";
import { NotificationProvider } from "./notifications/NotificationContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import SeoLoader from "./seo/SeoLoader";

function AppWrapper() {
    return (
        <Layout>
            <Outlet />
        </Layout>
    );
}

function RequireAuth({ children }) {
    const location = useLocation();
    const { isAuthenticated, ready } = useAuth();

    useEffect(() => {
        if (ready && !isAuthenticated) {
            const nextPath = `${location.pathname}${location.search}${location.hash}`;
            window.dispatchEvent(new CustomEvent("auth:open-login-modal", { detail: { nextPath } }));
        }
    }, [ready, isAuthenticated, location.pathname, location.search, location.hash]);

    if (!ready) {
        return (
            <div className="blog-page-root">
                <div className="blog-page-inner">
                    <article className="cms-page-panel page-auth-panel">
                        <p className="cms-page-loading">Проверяем авторизацию...</p>
                    </article>
                </div>
            </div>
        );
    }
    if (!isAuthenticated) {
        return (
            <div className="blog-page-root">
                <div className="blog-page-inner">
                    <article className="cms-page-panel page-auth-panel">
                        <h2 className="cms-page-title">Требуется авторизация</h2>
                        <p className="page-status-message">Войдите в аккаунт, чтобы открыть эту страницу.</p>
                    </article>
                </div>
            </div>
        );
    }
    return children;
}

function ScrollToTop() {
    const location = useLocation();

    useEffect(() => {
        if (location.hash) return;
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [location.pathname, location.search, location.hash]);

    return null;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<AppWrapper />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="map" element={<MapPage />} />
                <Route path="fishes/:slug" element={<FishDetailPage />} />
                <Route path="fishes" element={<FishesPage />} />
                <Route path="blog/:slug" element={<BlogArticlePage />} />
                <Route path="blog" element={<BlogPage />} />
                <Route path="tools" element={<ToolsPage />} />
                <Route path="depth" element={<Navigate to="/tools" replace />} />
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="tools/fish-detection" element={<FishDetectionPage />} />
                <Route path="tools/sonar-upload" element={<SonarUploadPage />} />
                <Route path="profile/*" element={<Profile />} />
                <Route
                    path="spots/:id/edit-suggestion"
                    element={(
                        <RequireAuth>
                            <EditSpotSuggestionPage />
                        </RequireAuth>
                    )}
                />
                <Route path="my-spots" element={<Navigate to="/profile/my-spots" replace />} />
                <Route path="my-suggestions" element={<Navigate to="/profile/suggestions" replace />} />
                <Route path="spots/:id" element={<PublicSpotPage />} />
                <Route
                    path="spots/new"
                    element={(
                        <RequireAuth>
                            <NewSpotPage />
                        </RequireAuth>
                    )}
                />
                {MESSAGING_ENABLED && MessagesPageLazy && (
                    <Route
                        path="messages"
                        element={(
                            <Suspense fallback={(
                                <section className="messages-page page-card">
                                    <p className="page-status-message">Загрузка...</p>
                                </section>
                            )}>
                                <MessagesPageLazy />
                            </Suspense>
                        )}
                    />
                )}
                <Route path="login" element={<Login />} />
                <Route path="register" element={<Register />} />
                <Route path="verify-email" element={<VerifyEmail />} />
                <Route path="forgot" element={<Forgot />} />
                <Route path="reset" element={<Reset />} />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
}

function AppProviders({ children }) {
    return (
        <HelmetProvider>
            <AuthProvider>
                <SettingsProvider>
                    <MessagesProvider>
                        <NotificationProvider>
                            <Router>
                                <SeoLoader />
                                <AnalyticsTracker />
                                <ScrollToTop />
                                {children}
                            </Router>
                        </NotificationProvider>
                    </MessagesProvider>
                </SettingsProvider>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default function App() {
    return (
        <AppProviders>
            <AppRoutes />
        </AppProviders>
    );
}
